import { useState, useEffect } from 'react'
import { Save, Loader2 } from 'lucide-react'
import api from '@/lib/api'

interface Config {
  hourly_cost_rate: number
  margin_pvp: number
  margin_pvd: number
  margin_vip: number
  vat_rate: number
  diagnostic_fee: number
  transport_fee_pvp: number
  transport_fee_pvd: number
  warranty_months: number
  pickup_shipping_cost: number
  baleares_shipping_cost: number
  quote_validity_days: number
  quote_reminder_days: number
  auto_close_days: number
  default_estimated_days_min: number
  default_estimated_days_max: number
  default_carrier_peninsula: string
  default_carrier_baleares: string
  notification_email: string
  is_active: boolean
}

export default function RepairsConfigTab() {
  const [config, setConfig] = useState<Config | null>(null)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')
  const [loadError, setLoadError] = useState('')

  useEffect(() => { loadConfig() }, [])

  const loadConfig = async () => {
    setLoadError('')
    try {
      const res = await api.get('/repairs/admin/config')
      setConfig(res.data)
    } catch (err: any) {
      console.error('Error loading config:', err)
      const detail = err.response?.data?.detail
      const msg = typeof detail === 'string' ? detail : err.message || 'Error al cargar la configuración'
      setLoadError(msg)
    }
  }

  const saveConfig = async () => {
    if (!config) return
    setSaving(true)
    setMessage('')
    try {
      await api.put('/repairs/admin/config', config)
      setMessage('Configuración guardada')
      setTimeout(() => setMessage(''), 3000)
    } catch (err) {
      setMessage('Error al guardar')
    } finally {
      setSaving(false)
    }
  }

  const updateField = (field: keyof Config, value: any) => {
    if (!config) return
    setConfig({ ...config, [field]: value })
  }

  if (loadError) return (
    <div className="p-6 text-center">
      <p className="text-red-500 mb-3">Error: {loadError}</p>
      <button onClick={loadConfig} className="px-4 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700">Reintentar</button>
    </div>
  )

  if (!config) return <div className="p-6 text-center text-slate-400">Cargando configuración...</div>

  const inputClass = "w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
  const labelClass = "block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6 overflow-y-auto h-full pb-12">
      {/* Módulo activo */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-slate-900 dark:text-white">Módulo de reparaciones</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">Activa o desactiva el servicio de reparaciones en el portal público</p>
          </div>
          <button
            onClick={() => updateField('is_active', !config.is_active)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${config.is_active ? 'bg-indigo-600' : 'bg-slate-300 dark:bg-slate-600'}`}
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${config.is_active ? 'translate-x-6' : 'translate-x-1'}`} />
          </button>
        </div>
      </div>

      {/* Tarifa técnico */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
        <h3 className="font-semibold text-slate-900 dark:text-white mb-4">Tarifa técnico</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Coste por hora trabajada (EUR/h)</label>
            <input type="number" step="0.50" value={config.hourly_cost_rate} onChange={e => updateField('hourly_cost_rate', parseFloat(e.target.value) || 0)} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Garantía de la reparación (meses)</label>
            <input type="number" min="1" value={config.warranty_months ?? 3} onChange={e => updateField('warranty_months', parseInt(e.target.value) || 3)} className={inputClass} />
            <p className="text-xs text-slate-500 mt-1">Pieza y mano de obra. Se muestra en presupuestos, emails y condiciones</p>
          </div>
        </div>
      </div>

      {/* Portes al cliente */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
        <h3 className="font-semibold text-slate-900 dark:text-white mb-1">Portes cobrados al cliente (IVA incluido)</h3>
        <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">Recogida + devolución por mensajería. Van como línea aparte en el presupuesto y son lo que se cobra si el cliente rechaza o el equipo no tiene arreglo. El diagnóstico es gratuito. En entregas en tienda no se cobran.</p>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Particulares (PVP)</label>
            <input type="number" step="0.05" value={config.transport_fee_pvp ?? 29.9} onChange={e => updateField('transport_fee_pvp', parseFloat(e.target.value) || 0)} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Distribuidores (PVD / VIP)</label>
            <input type="number" step="0.05" value={config.transport_fee_pvd ?? 11.9} onChange={e => updateField('transport_fee_pvd', parseFloat(e.target.value) || 0)} className={inputClass} />
            <p className="text-xs text-slate-500 mt-1">5,95 € recogida + 5,95 € devolución</p>
          </div>
        </div>
      </div>

      {/* Márgenes */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
        <h3 className="font-semibold text-slate-900 dark:text-white mb-4">Márgenes por tipo de cliente</h3>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className={labelClass}>PVP (público) %</label>
            <input type="number" step="1" value={config.margin_pvp} onChange={e => updateField('margin_pvp', parseFloat(e.target.value) || 0)} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>PVD (distribuidor) %</label>
            <input type="number" step="1" value={config.margin_pvd} onChange={e => updateField('margin_pvd', parseFloat(e.target.value) || 0)} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>VIP %</label>
            <input type="number" step="1" value={config.margin_vip} onChange={e => updateField('margin_vip', parseFloat(e.target.value) || 0)} className={inputClass} />
          </div>
        </div>
      </div>

      {/* IVA */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
        <h3 className="font-semibold text-slate-900 dark:text-white mb-4">IVA</h3>
        <div className="w-1/3">
          <label className={labelClass}>IVA %</label>
          <input type="number" step="0.5" value={config.vat_rate} onChange={e => updateField('vat_rate', parseFloat(e.target.value) || 0)} className={inputClass} />
        </div>
      </div>

      {/* Transporte */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
        <h3 className="font-semibold text-slate-900 dark:text-white mb-4">Transporte</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Coste MRW por trayecto península (EUR)</label>
            <input type="number" step="0.10" value={config.pickup_shipping_cost} onChange={e => updateField('pickup_shipping_cost', parseFloat(e.target.value) || 0)} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Coste por trayecto Baleares (EUR)</label>
            <input type="number" step="0.10" value={config.baleares_shipping_cost} onChange={e => updateField('baleares_shipping_cost', parseFloat(e.target.value) || 0)} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Transportista península</label>
            <select value={config.default_carrier_peninsula} onChange={e => updateField('default_carrier_peninsula', e.target.value)} className={inputClass}>
              <option value="mrw">MRW</option>
              <option value="gls">GLS</option>
              <option value="ctt">CTT</option>
              <option value="dhl">DHL</option>
            </select>
          </div>
          <div>
            <label className={labelClass}>Transportista Baleares</label>
            <select value={config.default_carrier_baleares} onChange={e => updateField('default_carrier_baleares', e.target.value)} className={inputClass}>
              <option value="ctt">CTT</option>
              <option value="mrw">MRW</option>
              <option value="gls">GLS</option>
              <option value="dhl">DHL</option>
            </select>
          </div>
        </div>
      </div>

      {/* Plazos */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
        <h3 className="font-semibold text-slate-900 dark:text-white mb-4">Plazos</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Validez presupuesto (días)</label>
            <input type="number" value={config.quote_validity_days} onChange={e => updateField('quote_validity_days', parseInt(e.target.value) || 15)} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Recordatorio a los (días)</label>
            <input type="number" value={config.quote_reminder_days} onChange={e => updateField('quote_reminder_days', parseInt(e.target.value) || 3)} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Cierre automático (días tras la entrega)</label>
            <input type="number" min="1" value={config.auto_close_days ?? 7} onChange={e => updateField('auto_close_days', parseInt(e.target.value) || 7)} className={inputClass} />
            <p className="text-xs text-slate-500 mt-1">El tracking marca la entrega; pasados estos días sin incidencia el caso pasa a Completada</p>
          </div>
        </div>
      </div>

      {/* Email */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
        <h3 className="font-semibold text-slate-900 dark:text-white mb-4">Email</h3>
        <div>
          <label className={labelClass}>Email de notificación</label>
          <input type="email" value={config.notification_email} onChange={e => updateField('notification_email', e.target.value)} className={inputClass} />
        </div>
      </div>

      {/* Save */}
      <div className="flex items-center gap-3">
        <button
          onClick={saveConfig}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-2.5 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Guardar configuración
        </button>
        {message && <span className={`text-sm ${message.includes('Error') ? 'text-red-500' : 'text-emerald-500'}`}>{message}</span>}
      </div>
    </div>
  )
}
